import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  ToggleGroup,
  ToggleGroupItem,
} from '@monksflow/monks-ui';
import {
  taxonomiesQueryOptions,
  taxonomyDetailQueryOptions,
  type TaxonomySummary,
} from '@/entities/taxonomy';
import type { TaxonomyCampaign } from '@/entities/taxonomy-campaign';
import { ImportFilePicker } from '@/entities/taxonomy-import';
import type { TaxonomyDataset } from '@/entities/taxonomy-dataset';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { FormDialog } from '@/shared/ui/FormDialog';
import { useCampaignSelection, type CampaignMode } from './useCampaignSelection';

interface NewTaxonomyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (payload: {
    name: string;
    description?: string;
    taxonomyId: string;
    taxonomyVersionId: string;
    /**
     * Already resolved to an id by the time it gets here. Creating the campaign
     * is the page's job, not the modal's — the modal only says which one, or
     * asks for a new one by name.
     */
    campaignId?: string | null;
  }) => void;
  /** Campaigns the new taxonomy may be filed under. */
  campaigns: TaxonomyCampaign[];
  /**
   * Creates a campaign and resolves to its id, so the page owns the mutation and
   * the modal stays a form. Rejecting leaves the modal open with nothing created.
   */
  onCreateCampaign: (input: { name: string; description?: string }) => Promise<string>;
  /**
   * Every taxonomy already visible here, so a duplicate name is refused before
   * the request rather than after it. The server enforces the same rule and a
   * unique index backs it; this is the half that can disable the button.
   */
  existing: TaxonomyDataset[];
  submitting?: boolean;
  /** A clash the server caught that the check below could not — a lost race. */
  serverError?: string | null;
  /**
   * Drops `serverError`. Called on the first edit after one arrives, so a
   * refusal cannot sit under a name the user has already changed.
   */
  onClearServerError?: () => void;
  /**
   * Starts an import from an uploaded spreadsheet: into the chosen schema, or building a new one
   * from it. A spreadsheet is offered only when this is given.
   */
  onImport?: (payload: {
    file: File;
    /** The published schema the rows fill, or null to build a new schema from the file first. */
    schema: { taxonomyId: string; versionId: string } | null;
    campaign: { id: string; name: string } | null;
  }) => void;
}

type SchemaMode = 'existing' | 'new';

const SCHEMA_MODES: readonly SchemaMode[] = ['existing', 'new'];
const CAMPAIGN_MODES: readonly CampaignMode[] = ['none', 'existing', 'new'];

/** How `uq_taxonomy_dataset_name` compares two names. */
function nameKey(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Create a taxonomy: pick a published schema (or, with a spreadsheet, build a new one), then a
 * campaign, then a name. A spreadsheet given with an existing schema fills that schema.
 *
 * Campaign is OPTIONAL and defaults to None. A taxonomy with no campaign is a
 * normal resting state, shown under "Ungrouped" on the list — not a gap to be
 * filled in later. Forcing a choice here would make someone invent a grouping
 * before they have one, and the very first taxonomy in a workspace has nothing
 * to choose from.
 *
 * Only PUBLISHED schemas are offered. A draft is editable in place, so a name
 * generated from one could change meaning after someone used it; the server
 * refuses it too, and this keeps the user from reaching that refusal.
 */
export function NewTaxonomyModal({
  open,
  onOpenChange,
  onCreate,
  campaigns,
  onCreateCampaign,
  existing,
  submitting = false,
  serverError = null,
  onClearServerError,
  onImport,
}: NewTaxonomyModalProps) {
  const { t } = useTranslation('knowledge');
  const spreadsheetEnabled = Boolean(onImport);
  const [schemaMode, setSchemaMode] = useState<SchemaMode>('existing');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const newSchema = spreadsheetEnabled && schemaMode === 'new';
  /** A new schema always comes from a spreadsheet; an existing one only when one is given. */
  const importing = newSchema || (spreadsheetEnabled && Boolean(file));
  const currentWorkspaceId = useCurrentWorkspaceId();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [schemaId, setSchemaId] = useState('');
  const campaign = useCampaignSelection();

  /**
   * Whether the modal is still open, readable from inside an await.
   *
   * `FormDialog`'s footer lock only disables its own two buttons; Escape, a
   * backdrop click and the dialog's own X still close it, and none of them can
   * cancel a request already in flight. Without this check the campaign resolves
   * after the dismissal, the taxonomy is created anyway, and the list page's
   * `onSuccess` navigates to a detail page for something the user believes they
   * cancelled.
   */
  const openRef = useRef(open);
  openRef.current = open;
  const campaignFieldId = useId();
  const newCampaignNameId = useId();
  const newCampaignDescriptionId = useId();
  const schemaFieldId = useId();
  const nameFieldId = useId();
  const nameErrorId = useId();
  const descriptionFieldId = useId();

  /**
   * One whole sentence per shape, rather than a markets fragment concatenated
   * onto a translated string.
   *
   * Four keys instead of two so a translator always sees a full sentence they
   * can reorder and repunctuate. Gluing `' · {{markets}}'` onto the end in JS
   * reads the same in English and is unlocalisable everywhere else.
   */
  function summaryLine(schema: TaxonomySummary, fieldCount: number | undefined): string {
    const markets = schema.market.join(', ');
    const base = { name: schema.name, version: schema.currentVersionNumber ?? '?' };

    if (fieldCount === undefined) {
      return markets
        ? t('taxonomy.generationList.schemaSummaryUnavailableWithMarkets', { ...base, markets })
        : t('taxonomy.generationList.schemaSummaryUnavailable', base);
    }
    return markets
      ? t('taxonomy.generationList.schemaSummaryWithMarkets', {
          ...base,
          count: fieldCount,
          markets,
        })
      : t('taxonomy.generationList.schemaSummary', { ...base, count: fieldCount });
  }

  /**
   * `staleTime: 0` so opening the picker always re-reads the schema list.
   *
   * The mutations that change a schema's status already invalidate this key, so a
   * publish in THIS client is reflected without help. What they cannot cover is a
   * schema published anywhere else — another tab, a colleague, or a direct API
   * call — which the app's global 5-minute `staleTime` would otherwise hide for up
   * to five minutes, with a reload as the only undiscoverable way out. This picker
   * gates a creation flow and opens on a deliberate click, so one request per open
   * is the right trade.
   */
  const query = useQuery({ ...taxonomiesQueryOptions(), enabled: open, staleTime: 0 });

  const publishedSchemas = useMemo<TaxonomySummary[]>(
    () => (query.data ?? []).filter((s) => s.status === 'published' && s.currentVersionId),
    [query.data],
  );

  const selected = publishedSchemas.find((s) => s.id === schemaId);

  /**
   * Only for the summary line's field count, which the list payload does not
   * carry — fields live on a version, and the summary endpoint returns none. One
   * request for the schema the user actually chose, rather than one per option.
   */
  const detail = useQuery({
    ...taxonomyDetailQueryOptions(schemaId),
    enabled: open && Boolean(selected),
  });

  const pinnedVersion = useMemo(
    () => detail.data?.versions.find((v) => v.id === selected?.currentVersionId),
    [detail.data, selected?.currentVersionId],
  );

  /**
   * Scoped to the selected schema AND the workspace the new taxonomy will land
   * in — the exact group `uq_taxonomy_dataset_name` keys on.
   *
   * The schema half is obvious: the same name under a different one is allowed.
   * The workspace half is the trap. `existing` is the VISIBILITY-scoped list, so
   * inside a workspace it also carries org-scoped rows — and matching on those
   * would refuse a name the server accepts, leaving Create disabled with no way
   * out. A check stricter than the constraint it predicts is worse than none;
   * the repository's own `takenNameKeys` carries the same warning.
   */
  const takenNames = useMemo(() => {
    if (!selected) {
      return new Set<string>();
    }
    // Mirrors `workspaceId IS NOT DISTINCT FROM`: an org-scoped row (null) only
    // collides with another org-scoped row.
    const scope = currentWorkspaceId ?? null;
    return new Set(
      existing
        .filter((d) => d.taxonomyId === selected.id && (d.workspaceId ?? null) === scope)
        .map((d) => nameKey(d.name)),
    );
  }, [existing, selected, currentWorkspaceId]);

  const trimmedName = name.trim();
  const duplicate = trimmedName.length > 0 && takenNames.has(nameKey(trimmedName));
  const nameError = duplicate ? t('taxonomy.generationList.nameTaken') : serverError;

  /*
   * Dropped on the first edit after it arrives, so a refusal cannot sit under a
   * name it no longer describes. Both halves of the uniqueness key count: the
   * schema matters as much as the name, and gating Create on `serverError`
   * without clearing it here left the button dead after a schema change.
   */
  function clearStaleRefusal() {
    if (serverError) {
      onClearServerError?.();
    }
  }

  function handleNameChange(next: string) {
    setName(next);
    clearStaleRefusal();
  }

  function handleSchemaChange(next: string) {
    setSchemaId(next);
    clearStaleRefusal();
  }

  /*
   * Pulled out rather than called as `campaign.reset()`. The hook returns a fresh
   * object every render, so depending on `campaign` would run this on every one —
   * and exhaustive-deps demands the whole object for a method call, because the
   * receiver is part of it. `reset` is `useCallback(..., [])`, so destructuring it
   * gives the effect a stable dependency and the rule stops asking, with nothing
   * suppressed.
   */
  const { reset: resetCampaign } = campaign;

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setSchemaId('');
      setSchemaMode('existing');
      setFile(null);
      setFileError(null);
      resetCampaign();
    }
  }, [open, resetCampaign]);

  /*
   * `serverError` gates this too. Without it the button stayed enabled after a
   * 409, so the same payload the server had just refused could be sent again
   * unchanged. Editing the name clears the error via `onClearServerError`,
   * which is what re-enables it.
   */
  const trimmedNewCampaign = campaign.newName.trim();

  /**
   * Each mode answers for itself: `existing` needs a pick, `new` needs a name,
   * `none` needs nothing. Without this, switching to New and leaving it blank
   * sent a campaign named `''`.
   */
  const campaignReady =
    campaign.mode === 'none' ||
    (campaign.mode === 'existing'
      ? Boolean(campaign.existingCampaignId)
      : Boolean(trimmedNewCampaign));

  const canCreate =
    Boolean(trimmedName && selected?.currentVersionId) &&
    campaignReady &&
    !duplicate &&
    !serverError &&
    !submitting &&
    !campaign.creating;

  /**
   * In `new` mode the campaign is created FIRST and its id passed on, as
   * specified — there is no endpoint that does both, and inventing one for a
   * two-step the user already understands would be the wrong seam.
   *
   * If that create fails the taxonomy is not attempted: the alternative is
   * silently filing it under Ungrouped, which reads as the campaign field
   * having been ignored.
   */
  async function handleCreate() {
    if (!selected?.currentVersionId || !canCreate) {
      return;
    }

    const resolvedCampaignId = await resolveCampaign();
    if (resolvedCampaignId === undefined) {
      return;
    }

    const trimmedDescription = description.trim();
    onCreate({
      name: trimmedName,
      ...(trimmedDescription ? { description: trimmedDescription } : {}),
      taxonomyId: selected.id,
      taxonomyVersionId: selected.currentVersionId,
      campaignId: resolvedCampaignId,
    });
    /*
     * Batched with the parent's own pending flag, so Create never flickers back
     * to enabled. It has to happen here rather than on close: if the dataset
     * create is refused, the modal STAYS open and a flag left set would leave
     * Create dead with no way to retry.
     */
    campaign.finishCampaignCreation();
  }

  const canImport =
    Boolean(file) &&
    !fileError &&
    (newSchema || Boolean(selected?.currentVersionId)) &&
    campaignReady &&
    !serverError &&
    !submitting &&
    !campaign.creating;

  async function handleImport() {
    if (!file || !canImport || !onImport) {
      return;
    }
    const resolvedCampaignId = await resolveCampaign();
    if (resolvedCampaignId === undefined) {
      return;
    }
    const campaignName =
      campaign.mode === 'new'
        ? trimmedNewCampaign
        : campaigns.find((c) => c.id === resolvedCampaignId)?.name;
    onImport({
      file,
      schema:
        newSchema || !selected?.currentVersionId
          ? null
          : { taxonomyId: selected.id, versionId: selected.currentVersionId },
      campaign:
        resolvedCampaignId && campaignName ? { id: resolvedCampaignId, name: campaignName } : null,
    });
    campaign.finishCampaignCreation();
  }

  function handlePick(picked: File | null, error: string | null) {
    setFile(picked);
    setFileError(error);
    clearStaleRefusal();
  }

  /**
   * The campaign id to file the new taxonomy under, creating it first in `new` mode. Undefined
   * means stop: the campaign could not be created, or the dialog closed while it was.
   */
  async function resolveCampaign(): Promise<string | null | undefined> {
    let resolvedCampaignId: string | null = null;
    if (campaign.mode === 'existing') {
      resolvedCampaignId = campaign.existingCampaignId;
    } else if (campaign.mode === 'new') {
      if (campaign.createdCampaignId) {
        resolvedCampaignId = campaign.createdCampaignId;
      } else {
        campaign.startCampaignCreation();
        try {
          const trimmedCampaignDescription = campaign.newDescription.trim();
          resolvedCampaignId = await onCreateCampaign({
            name: trimmedNewCampaign,
            ...(trimmedCampaignDescription ? { description: trimmedCampaignDescription } : {}),
          });
          campaign.markCampaignCreated(resolvedCampaignId);
        } catch {
          // The mutation surfaces its own failure; the modal stays open so the
          // user can retry without retyping the taxonomy.
          campaign.finishCampaignCreation();
          return undefined;
        }
        /*
         * Deliberately NOT cleared here. Clearing it between the campaign
         * landing and `onCreate` running would re-enable Create for exactly the
         * window the flag exists to close. It is cleared below, in the same
         * React batch as the submit, so the render that sees it false already
         * sees `submitting` true.
         */
      }
    }

    /*
     * Dismissed while the campaign was being created. The campaign itself is
     * kept — it exists, it is valid, and it will appear in the list — but the
     * taxonomy is not created and nothing navigates.
     */
    if (!openRef.current) {
      campaign.finishCampaignCreation();
      return undefined;
    }
    return resolvedCampaignId;
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('taxonomy.generationList.newTaxonomy')}
    >
      <FormDialog.Body>
        <div className="flex flex-col gap-md">
          <div className="flex flex-col gap-xs">
            {/*
              Schema leads: most taxonomies fill a schema the client already approved, so the
              existing one is the default and a new one from a spreadsheet the exception.
            */}
            <Label htmlFor={schemaFieldId}>
              {t('taxonomy.generationList.schema')} <span className="text-destructive">*</span>
            </Label>
            {spreadsheetEnabled && (
              <ToggleGroup
                type="single"
                value={schemaMode}
                onValueChange={(next) => {
                  // Radix emits '' when the active item is pressed again; keep the current one.
                  if (next) {
                    setSchemaMode(next as SchemaMode);
                    clearStaleRefusal();
                  }
                }}
                aria-label={t('taxonomy.generationList.schema')}
                spacing={1}
                size="sm"
                className="w-fit rounded-lg border border-border-light bg-secondary p-0.5"
              >
                {SCHEMA_MODES.map((mode) => (
                  <ToggleGroupItem
                    key={mode}
                    value={mode}
                    className="rounded-md px-3 text-muted-foreground data-[state=on]:bg-card data-[state=on]:text-foreground"
                  >
                    {mode === 'existing'
                      ? t('taxonomy.import.schemaExisting')
                      : t('taxonomy.import.schemaNew')}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            )}

            {!newSchema && (
              <>
                <Select value={schemaId} onValueChange={handleSchemaChange}>
                  <SelectTrigger id={schemaFieldId} className="bg-input">
                    <SelectValue placeholder={t('taxonomy.generationList.selectSchema')} />
                  </SelectTrigger>
                  <SelectContent>
                    {publishedSchemas.map((schema) => (
                      <SelectItem key={schema.id} value={schema.id}>
                        {t('taxonomy.generationList.schemaOption', {
                          name: schema.name,
                          version: schema.currentVersionNumber ?? '?',
                        })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {!query.isLoading && publishedSchemas.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    {t('taxonomy.generationList.noPublishedSchemas')}
                  </p>
                )}

                {/*
                  Confirms the choice rather than helping scan the list, which is why it sits
                  here and not inside each option: the field count costs a request, and one for
                  the chosen schema beats one per option.
                */}
                {selected && detail.isLoading && <Skeleton className="h-5 w-64" />}
                {selected && !detail.isLoading && (
                  <p className="text-sm text-muted-foreground">
                    {/*
                      A failed fetch must not read as a real answer. The app's query client does
                      not retry, so `detail.data` stays undefined after one network error, and
                      defaulting the count to 0 rendered "0 fields" as though the schema
                      genuinely had none.
                    */}
                    {summaryLine(selected, pinnedVersion?.fields.length)}
                  </p>
                )}
              </>
            )}
          </div>

          {spreadsheetEnabled && (
            <div className="flex flex-col gap-xs">
              {!newSchema && <Label>{t('taxonomy.import.fillFromSpreadsheet')}</Label>}
              <ImportFilePicker
                file={file}
                error={fileError ?? serverError}
                onPick={handlePick}
                disabled={submitting}
              />
            </div>
          )}

          <div className="flex flex-col gap-xs">
            <Label htmlFor={campaignFieldId}>{t('taxonomy.campaign.label')}</Label>
            {/*
              `type="single"` emits the same `radiogroup`/`radio`/`aria-checked` this was
              hand-rolling, and adds the roving tabindex and arrow keys the hand-rolled version
              never had.
            */}
            <ToggleGroup
              type="single"
              value={campaign.mode}
              onValueChange={(next) => {
                /*
                  Radix emits '' when the active item is pressed again. A taxonomy is always in
                  exactly one of these modes, so a deselect is not a state this form has: keep
                  the current one, as `PropControl` and `RangeToggle` do for the same reason.
                */
                if (next) {
                  campaign.setMode(next as CampaignMode);
                }
              }}
              aria-label={t('taxonomy.campaign.label')}
              spacing={1}
              size="sm"
              className="w-fit rounded-lg border border-border-light bg-secondary p-0.5"
            >
              {CAMPAIGN_MODES.map((mode) => (
                <ToggleGroupItem
                  key={mode}
                  value={mode}
                  /*
                    Existing is unreachable with nothing to pick: the very first taxonomy in a
                    workspace has no campaigns yet, and an enabled control opening an empty list
                    is a dead end.
                  */
                  disabled={mode === 'existing' && campaigns.length === 0}
                  className="rounded-md px-3 text-muted-foreground data-[state=on]:bg-card data-[state=on]:text-foreground"
                >
                  {t(`taxonomy.campaign.${mode}`)}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>

            {campaign.mode === 'existing' && (
              <Select
                value={campaign.existingCampaignId}
                onValueChange={campaign.setExistingCampaignId}
              >
                <SelectTrigger id={campaignFieldId} className="bg-input">
                  <SelectValue placeholder={t('taxonomy.campaign.selectPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {campaign.mode === 'new' && (
              <div className="flex flex-col gap-xs">
                <Input
                  id={newCampaignNameId}
                  value={campaign.newName}
                  onChange={(e) => campaign.setNewName(e.target.value)}
                  placeholder={t('taxonomy.campaign.namePlaceholder')}
                  aria-label={t('taxonomy.campaign.nameLabel')}
                  maxLength={255}
                  className="bg-input"
                />
                <Textarea
                  id={newCampaignDescriptionId}
                  value={campaign.newDescription}
                  onChange={(e) => campaign.setNewDescription(e.target.value)}
                  placeholder={t('taxonomy.campaign.descriptionPlaceholder')}
                  aria-label={t('taxonomy.campaign.descriptionLabel')}
                  maxLength={2000}
                  rows={2}
                  className="bg-input"
                />
              </div>
            )}
          </div>

          {!importing && (
            <>
              <div className="flex flex-col gap-xs">
                <Label htmlFor={nameFieldId}>
                  {t('taxonomy.generationList.name')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id={nameFieldId}
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder={t('taxonomy.generationList.namePlaceholder')}
                  maxLength={255}
                  className="bg-input"
                  aria-invalid={nameError ? true : undefined}
                  aria-describedby={nameError ? nameErrorId : undefined}
                />
                {nameError && (
                  <p id={nameErrorId} role="alert" className="text-xs text-destructive">
                    {nameError}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-xs">
                <Label htmlFor={descriptionFieldId}>
                  {t('taxonomy.generationList.description')}
                </Label>
                <Textarea
                  id={descriptionFieldId}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('taxonomy.generationList.descriptionPlaceholder')}
                  maxLength={2000}
                  rows={3}
                  className="bg-input"
                />
              </div>
            </>
          )}
        </div>
      </FormDialog.Body>
      <FormDialog.Footer
        primary={
          importing
            ? {
                label: t('taxonomy.import.read'),
                onClick: () => void handleImport(),
                disabled: !canImport,
                loading: submitting || campaign.creating,
              }
            : {
                label: t('taxonomy.generationList.create'),
                onClick: () => void handleCreate(),
                disabled: !canCreate,
                loading: submitting || campaign.creating,
              }
        }
        secondary={{
          label: t('taxonomy.generationList.cancel'),
          onClick: () => onOpenChange(false),
        }}
      />
    </FormDialog>
  );
}
